import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useMe } from '../../lib/me';
import { dayWords } from '../../lib/people';

const API = process.env.REACT_APP_API_URL;
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

const STATE_LABEL = { now: 'Away now', upcoming: 'Coming up', past: 'Back', cancelled: 'Cancelled' };
const STATE_BADGE = { now: 'badge-on-hold', upcoming: 'badge-in-progress', past: 'badge-closed', cancelled: 'badge-closed' };

/** From, to, who decides, and why - the same four fields to set or change. */
function AbsenceFields({ value, onChange, approvers, who, lockWho = false }) {
  const delegates = approvers.filter((a) => a.id !== value.user_id);
  return (
    <div className="pp-grid">
      {!lockWho && (
        <label className="pp-field">
          <span>Who is away</span>
          <select value={value.user_id} aria-label="Who is away"
                  onChange={(e) => onChange({ ...value, user_id: e.target.value, delegate_id: value.delegate_id === e.target.value ? '' : value.delegate_id })}>
            <option value="">Choose…</option>
            {approvers.map((a) => <option key={a.id} value={a.id}>{a.name}{a.id === who ? ' (you)' : ''}</option>)}
          </select>
        </label>
      )}
      <label className="pp-field">
        <span>From</span>
        <input type="date" value={value.starts_on} aria-label="Away from"
               onChange={(e) => onChange({ ...value, starts_on: e.target.value, ends_on: value.ends_on && value.ends_on < e.target.value ? e.target.value : value.ends_on })} />
      </label>
      <label className="pp-field">
        <span>Until (last day away)</span>
        <input type="date" value={value.ends_on} min={value.starts_on || undefined} aria-label="Away until"
               onChange={(e) => onChange({ ...value, ends_on: e.target.value })} />
      </label>
      <label className="pp-field">
        <span>Who decides meanwhile</span>
        <select value={value.delegate_id} aria-label="Who decides meanwhile"
                onChange={(e) => onChange({ ...value, delegate_id: e.target.value })}>
          <option value="">Choose an approver…</option>
          {delegates.map((a) => <option key={a.id} value={a.id}>{a.name}{a.away ? ' (away now)' : ''}</option>)}
        </select>
      </label>
      <label className="pp-field pp-wide">
        <span>Note (optional)</span>
        <input type="text" value={value.note} maxLength={500} placeholder="e.g. Annual leave"
               onChange={(e) => onChange({ ...value, note: e.target.value })} />
      </label>
    </div>
  );
}

/**
 * APPROVERS AWAY (113): an approver - or an administrator for them - says
 * when they are away and who decides meanwhile. While it runs their approval
 * emails and the digest go to the delegate, the delegate may decide their
 * tickets and sign off their hours, and each decision says it was on their
 * behalf. The delegate is another approver: approving is an administrator's
 * act in Jarvis.
 */
export default function ApproversAway() {
  const me = useMe();
  const [data, setData] = useState(null);
  const [past, setPast] = useState(false);
  const [problem, setProblem] = useState('');
  const [said, setSaid] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);      // { id, ...fields }
  const [cancelAsk, setCancelAsk] = useState(null);

  const load = useCallback(() => axios.get(`${API}/people/absences`, { params: past ? { past: 1 } : {} })
    .then((r) => { setData(r.data); setProblem(''); })
    .catch((e) => { setData({ absences: [], approvers: [] }); setProblem(errorOf(e, 'Could not load who is away.')); }), [past]);
  useEffect(() => { load(); }, [load]);

  const approvers = useMemo(() => data?.approvers || [], [data]);
  const iApprove = approvers.some((a) => a.id === me?.id);
  const blank = () => ({ user_id: iApprove ? me.id : '', starts_on: data?.today || '', ends_on: '', delegate_id: '', note: '' });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setProblem(''); setSaid('');
    try {
      const r = await axios.post(`${API}/people/absences`, { ...form, note: form.note.trim() || null });
      const a = r.data.absence;
      setSaid(`${a.name} is away ${dayWords(a.starts_on)} to ${dayWords(a.ends_on)}; ${a.delegate_name} decides meanwhile.`);
      setForm(null);
      await load();
    } catch (err) {
      setProblem(errorOf(err, 'Could not set that absence.'));
    } finally { setBusy(false); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true); setProblem(''); setSaid('');
    try {
      const { id, starts_on: s, ends_on: en, delegate_id: dlg, note } = editing;
      await axios.patch(`${API}/people/absences/${id}`, { starts_on: s, ends_on: en, delegate_id: dlg, note: (note || '').trim() || null });
      setSaid('Absence changed.');
      setEditing(null);
      await load();
    } catch (err) {
      setProblem(errorOf(err, 'Could not change that absence.'));
    } finally { setBusy(false); }
  };

  const cancel = async (a) => {
    setBusy(true); setProblem(''); setSaid(''); setCancelAsk(null);
    try {
      await axios.delete(`${API}/people/absences/${a.id}`);
      setSaid(`${a.name}'s approvals go to them again.`);
      await load();
    } catch (err) {
      setProblem(errorOf(err, 'Could not cancel that absence.'));
    } finally { setBusy(false); }
  };

  const absences = data?.absences || [];
  return (
    <section className="pp-section" aria-label="Approvers away">
      <p className="pp-help pp-lead">
        While an approver is away, their approval emails and the morning digest go to whoever they name, and that
        person may approve, reject and sign off hours for them &mdash; each recorded as decided on their behalf.
        Tickets stay named to the approver, so whatever is left comes back to them.
      </p>
      <div className="pp-toolbar">
        <label className="pp-check">
          <input type="checkbox" checked={past} onChange={(e) => setPast(e.target.checked)} />
          <span>Show the last 90 days and cancelled ones</span>
        </label>
        <span className="pp-spacer" />
        {!form && (
          <button type="button" className="btn btn-primary" disabled={!data || approvers.length < 2}
                  onClick={() => { setForm(blank()); setSaid(''); }}>
            {iApprove ? 'Set an absence' : 'Set an approver away'}
          </button>
        )}
      </div>
      {data && approvers.length < 2 && (
        <p className="pp-help">A delegate has to be another approver, and there is only one. Make somebody else an approver in Settings → Users first.</p>
      )}
      {form && (
        <form className="pp-card" onSubmit={save} aria-label="Set an absence" noValidate>
          <h2 className="pp-card-title">Set an absence</h2>
          <AbsenceFields value={form} onChange={setForm} approvers={approvers} who={me?.id} />
          <div className="pp-actions">
            <button type="submit" className="btn btn-primary" disabled={busy || !form.user_id || !form.starts_on || !form.ends_on || !form.delegate_id}>
              {busy ? 'Saving…' : 'Save absence'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setForm(null)}>Cancel</button>
          </div>
        </form>
      )}
      <p className="pp-flash" role="status" aria-live="polite">{said}</p>
      {problem && <p className="pp-problem" role="alert">{problem}</p>}
      {data === null ? <p className="pp-quiet">Loading…</p> : absences.length === 0 ? (
        <div className="empty-state pp-empty"><p>No approver is away, or about to be.</p></div>
      ) : (
        <ul className="pp-absences" aria-label="Absences">
          {absences.map((a) => (
            <li key={a.id} className={`pp-card pp-absence is-${a.state}`} data-absence={a.id} data-state={a.state}>
              {editing?.id === a.id ? (
                <form onSubmit={saveEdit} aria-label={`Change ${a.name}'s absence`}>
                  <h3 className="pp-sub">{a.name}</h3>
                  <AbsenceFields value={editing} onChange={setEditing} approvers={approvers} who={me?.id} lockWho />
                  <div className="pp-actions">
                    <button type="submit" className="btn btn-small btn-primary" disabled={busy}>Save</button>
                    <button type="button" className="btn btn-small btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="pp-absence-head">
                    <strong className="pp-absence-name">{a.name}</strong>
                    <span className={`badge ${STATE_BADGE[a.state]}`}>{STATE_LABEL[a.state]}</span>
                  </div>
                  <p className="pp-absence-dates">
                    {a.starts_on === a.ends_on ? dayWords(a.starts_on) : `${dayWords(a.starts_on)} to ${dayWords(a.ends_on)}`}
                    {a.note ? ` · ${a.note}` : ''}
                  </p>
                  <p className="pp-absence-cover" data-cover="">
                    {a.state === 'now' && a.uncovered
                      ? <span className="pp-warn">{a.delegate_name} is away too, and nobody in the chain is in: their approvals go to every approver who is.</span>
                      : a.state === 'now' && a.covered_by && a.covered_by.id !== a.delegate_id
                        ? <>Named {a.delegate_name}, who is away too: <strong>{a.covered_by.name}</strong> decides today.</>
                        : <><strong>{a.delegate_name}</strong> decides{a.state === 'now' ? '' : ' while they are away'}.</>}
                  </p>
                  {(a.state === 'now' || a.state === 'upcoming') && (
                    cancelAsk === a.id ? (
                      <div className="pp-confirm" role="group" aria-label={`Cancel ${a.name}'s absence?`}>
                        <p>{a.state === 'now' ? `${a.name} is back? Their approvals go to them again straight away.` : `Cancel ${a.name}'s absence?`}</p>
                        <div className="pp-actions">
                          <button type="button" className="btn btn-small btn-primary" onClick={() => cancel(a)}>
                            {a.state === 'now' ? 'Yes, back now' : 'Cancel it'}
                          </button>
                          <button type="button" className="btn btn-small btn-secondary" onClick={() => setCancelAsk(null)}>No</button>
                        </div>
                      </div>
                    ) : (
                      <div className="pp-actions">
                        <button type="button" className="btn btn-small btn-secondary" disabled={busy}
                                onClick={() => setEditing({ id: a.id, user_id: a.user_id, starts_on: a.starts_on, ends_on: a.ends_on, delegate_id: a.delegate_id, note: a.note || '' })}>
                          Change
                        </button>
                        <button type="button" className="btn btn-small btn-secondary" disabled={busy} onClick={() => setCancelAsk(a.id)}>
                          {a.state === 'now' ? 'Back early' : 'Cancel'}
                        </button>
                      </div>
                    )
                  )}
                  {a.state === 'cancelled' && a.cancelled_by_name && <p className="pp-quiet">Cancelled by {a.cancelled_by_name}</p>}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
